// Customizable Area Start
import React, { CSSProperties, ChangeEvent } from "react";
import { createStyles, Theme } from "@material-ui/core/styles";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  FormControl,
  FormHelperText,
  withStyles,
  TextareaAutosize,
  styled,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { Formik } from "formik";
import Select from "react-select";
import InviteFriendsModalController, {
  Props,
} from "./InviteFriendsModalController.web";
import { InviteFriendsStepType } from "./enums";
import { backArrow } from "./assets";

const styles = (theme: Theme) =>
  createStyles({
    modal: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: "22px",
      flexShrink: 0,
      maxWidth: "555px",
      padding: "60px 0 40px",
      borderRadius: "8px 8px 32px 8px",
      background: "#FFF",
      "& *": {
        fontFamily: "Inter, 'sans-serif'"
      },
      [theme.breakpoints.down("sm")]: {
        minWidth: "320px",
        minHeight: "100%",
      },
      [theme.breakpoints.down(580)]: {
        width: "100%", 
        padding: "30px 16px",
      },
      [theme.breakpoints.down(650)]: {
        borderRadius: "0",
        paddingTop: '0',
        paddingLeft: "0",
        paddingRight: "0"
      }
    },

    closeButton: {
      position: "absolute",
      top: "48px",
      right: "11px",
      [theme.breakpoints.down("sm")]: {
        top: "16px",
        right: "8px",
      },
      [theme.breakpoints.down(650)]: {
        display: "none"
      }
    },

    modalTitle: {
      padding: "0 64px",
      color: "#000",
      textAlign: "center",
      fontSize: "24px",
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "32px",
      letterSpacing: "-0.12px",
      [theme.breakpoints.down(650)]: {
        padding: "0 12px"
      }
    },

    modalDescription: {
      padding: "0 64px",
      color: "#64748B",
      textAlign: "center",
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "22px",
      [theme.breakpoints.down(650)]: {
        padding: "0 24px"
      }
    },

    linkContainer: {
      display: "flex",
      justifyContent: "center",
      padding: "0 64px",
      width: "100%",
      height: "66px",
      flexShrink: 0,
      backgroundColor: "#FFC629",
      [theme.breakpoints.down(650)]: {
        padding: "0 44px",
        flexDirection: "column"
      }
    },

    linkText: {
      display: "flex",
      alignItems: "center",
      color: "#0F172A",
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "22px",
      textAlign: "center",
      [theme.breakpoints.down(650)]: {
        display: "inline"
      }
    },

    link: {
      display: "flex",
      alignItems: "center",
      color: "#0F172A",
      textDecoration: "none",
      fontWeight: 700,
      cursor: "pointer",
      [theme.breakpoints.down(650)]: {
        display: "inline"
      },
      "&:hover": {
        cursor: "pointer",
      },
    },

    form: {
      width: "100%",
      padding: "0 64px",
      [theme.breakpoints.down(650)]: {
        padding: "0 24px"
      }
    },

    formContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "12px",
    },

    inputLabel: {
      color: "#334155",
      fontSize: "14px",
      fontWeight: 700,
      lineHeight: "22px",
    },

    contactSelect: {
      width: "100%",
      height: "44px",
      fontSize: "16px",
      "& input::placeholder": {
        color: "#64748B",
        opacity: 1
      },
      "& .invite_contact__control": {
        borderRadius: "8px"
      }
    },

    checkBox: {
      "&.MuiCheckbox-colorSecondary.Mui-checked": {
        color: "#000",
      },
    },

    textInputContainer: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "4px",
    },

    templateInput: {
      width: "100%",
      flexShrink: 0,
      resize: "none",
      padding: "12px",
      border: "1px solid #CBD5E1",
      borderRadius: "8px",
      fontFamily: "inherit",
      fontSize: "16px",
      fontWeight: 600,
      lineHeight: "26px",
      color: "#475569",

      "&:focus": {
        outline: "none",
        border: "2px solid #FFC629",
      },
    },

    automationsButton: {
      display: "flex",
      padding: "16px",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#000000",
      borderRadius: "8px",
      color: "#FFF",
      fontSize: "16px",
      fontWeight: 700,
      lineHeight: "24px",
      textTransform: "none",

      "&:hover": {
        backgroundColor: "#000000",
      },
    },

    linkInput: {
      color: "#64748B",
      "& input": {
        padding: "0px"
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "1px solid #CBD5E1",
        borderRadius: "8px",
      },
    },

    submitButton: {
      display: "flex",
      padding: "16px 60px",
      justifyContent: "center",
      alignItems: "center",
      textTransform: "none",
      borderRadius: "8px",
      backgroundColor: "#FFC629",
      color: "#0F172A",
      fontSize: "16px",
      fontWeight: 700,
      lineHeight: "24px",

      "&:hover": {
        backgroundColor: "#FFC629",
      },
      [theme.breakpoints.down("sm")]: {
        padding: "12px 30px",
        fontSize: "14px",
      },
      [theme.breakpoints.down(650)]: {
        alignSelf: "center"
      }
    },
  });

const webStyles: Record<string, CSSProperties> = {
  linkInput: {
    display: "flex",
    width: "100%",
    height: "44px",
    padding: "12px",
    alignItems: "center",
    flexShrink: 0,
    color: "#64748B"
  },
};

const YellowTopNavigationBarMobile = styled(Box)(({theme}) => ({
  padding: "0 18px",
  heigth: "44px",
  width: "100%",
  display: "flex",
  alignItems: "center",
  "& p": {
    marginLeft: "24px",
    fontWeight: "bold",
    fontSize: "18px"
  },
  backgroundColor: "#ffc629",
  [theme.breakpoints.up(650)]: {
    display: "none"
  }
}))

class InviteFriendsModal extends InviteFriendsModalController {
  constructor(props: Props) {
    super(props);
  }

  render() {
    const {
      contacts,
      inviteMessage,
      link,
      classes,
      setStep,
      setInviteMessage,
      setLink,
      closeModal,
      handleSubmitInvite,
      checkContact,
    } = this.props;

    return (
      <Container
        id="inviteFriendsContainer"
        maxWidth={false}
        className={classes.modal}
        onClick={(e) => e.stopPropagation()}
      >
        <YellowTopNavigationBarMobile>
          <img src={backArrow} alt="" onClick={closeModal}/>
          <p>Invite a friend</p>
        </YellowTopNavigationBarMobile>
        <IconButton
          id="closeModalButton"
          className={classes.closeButton}
          onClick={closeModal}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.modalTitle}>
          {this.labelInviteFriendToEverybrand}
        </Typography>
        <Typography className={classes.modalDescription}>
          {this.inviteFriendDescriptionText}
        </Typography>
        <Box className={classes.linkContainer}>
          <Typography className={classes.linkText}>
            {this.bulkInviteProposal}
            <span
              id="bulkInviteLink"
              onClick={() => setStep(InviteFriendsStepType.BULK_INVITE)}
              className={classes.link}
            >
              &nbsp;{this.bulkInviteLink}
            </span>
          </Typography>
        </Box>
        <Box className={classes.linkContainer}>
          <Typography className={classes.linkText}>
            {this.addContactQuestion}
            <span
              id="addContactLink"
              onClick={() => setStep(InviteFriendsStepType.ADD_CONTACT)}
              className={classes.link}
            >
              &nbsp;{this.addContactLink}
            </span>
          </Typography>
        </Box>
        <Formik
          initialValues={{
            selectedContacts: [],
            invite_message: inviteMessage,
            link: link,
          }}
          validate={this.validate}
          onSubmit={(_values, actions) => {
            actions.setSubmitting(false);
            handleSubmitInvite();
          }}
        >
          {({ handleChange, handleSubmit, setFieldValue, errors, values }) => {
            const selectedContactsIds = contacts
              .filter((contact) => contact.checked)
              .map((contact) => contact.id);

            if (
              selectedContactsIds?.join("") !==
              values.selectedContacts?.join("")
            ) {
              setFieldValue(
                "selectedContacts",
                contacts
                  .filter((contact) => contact.checked)
                  .map((contact) => contact.id)
              );
            }

            return (
              <form onSubmit={handleSubmit} className={classes.form}>
                <Box className={classes.formContainer}>
                  <Typography className={classes.inputLabel}>
                    {this.sendTo}
                  </Typography>
                  <FormControl
                    fullWidth
                    error={Boolean(errors.selectedContacts)}
                  >
                    <Select
                      id="selectedContacts"
                      name="selectedContacts"
                      className={classes.contactSelect}
                      isSearchable={false}
                      closeMenuOnSelect={false}
                      classNamePrefix="invite_contact"
                      styles={{
                        control: (baseStyles, state) => ({
                          ...baseStyles,
                          height: "100%",
                          boxShadow: "unset",
                          borderColor: "#CBD5E1",
                          "&:hover": {
                            borderColor: "#CBD5E1 !important",
                            boxShadow: "#FFC629 !important",
                          },
                          "&.is-focused": {
                            borderColor: "#FFC629 !important",
                            boxShadow: "#FFC629 !important",
                          },
                          "& > div:first-child": {
                            overflow: "hidden",
                            height: "100%",
                          },
                        }),
                      }}
                      placeholder="Select contact"
                      isMulti
                      options={contacts.map((contact) => ({
                        value: contact.id,
                        label: contact.fullName,
                      }))}
                      value={contacts
                        .filter((contact) => contact.checked)
                        .map((contact) => ({
                          value: contact.id,
                          label: contact.fullName,
                        }))}
                      onChange={(selectedContacts) => {
                        checkContact(
                          Array.isArray(selectedContacts) && selectedContacts.map((contact) => contact.value) || []
                        );

                        const changedEvent = {
                          target: {
                            name: "selectedContacts",
                            value: contacts
                              .filter((contact) => contact.checked)
                              .map((contact) => contact.id),
                          },
                        };
                        handleChange(changedEvent);
                      }}
                    />
                    <FormHelperText>{errors.selectedContacts}</FormHelperText>
                  </FormControl>

                  <Box className={classes.textInputContainer}>
                    <Typography className={classes.inputLabel}>
                      {this.templatePreview}
                    </Typography>
                    <FormControl
                      fullWidth
                      error={Boolean(errors.invite_message)}
                    >
                      <TextareaAutosize
                        disabled
                        minRows={5}
                        id="invite_message"
                        name="invite_message"
                        className={classes.templateInput}
                        value={values.invite_message}
                        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                          setInviteMessage(event.target.value);
                          handleChange(event);
                        }}
                        style={
                          errors.invite_message
                            ? { borderColor: "#f44336" }
                            : {}
                        }
                      />
                    </FormControl>
                    <FormHelperText
                      style={errors.invite_message ? { color: "#f44336" } : {}}
                    >
                      {errors.invite_message}
                    </FormHelperText>
                  </Box>

                  <Box className={classes.textInputContainer}>
                    <Typography className={classes.inputLabel}>
                      {this.autolink}
                    </Typography>
                    <TextField
                      id="link"
                      disabled
                      name="link"
                      variant="outlined"
                      className={classes.linkInput}
                      value={values.link}
                      fullWidth
                      error={Boolean(errors.link)}
                      helperText={errors.link}
                      onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                        setLink(event.target.value);
                        handleChange(event);
                      }}
                      InputProps={{
                        style: webStyles.linkInput,
                      }}
                    />
                  </Box>
                  <Button
                    id="submitInvite"
                    variant="contained"
                    type="submit"
                    disableElevation
                    className={classes.submitButton}
                  >
                    {this.sendInvite}
                  </Button>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Container>
    );
  }
}

export { InviteFriendsModal };
export default withStyles(styles)(InviteFriendsModal);
// Customizable Area End
